<template>
  <div class="services-search-filters">
    <b-button
      variant="outline-secondary"
      size="sm"
      class="d-inline-flex align-items-center"
      :disabled="disabled"
      @click="showModal = true"
    >
      <feather-icon
        icon="FilterIcon"
        size="16"
        class="mr-50"
      />
      <span>Filters</span>
      <b-badge
        v-if="hasActiveFilters"
        variant="primary"
        class="ml-50"
      >
        Active
      </b-badge>
      <b-spinner
        v-if="isTableLoading"
        small
        class="ml-50"
        label="Loading..."
      />
    </b-button>

    <b-modal
      id="services-search-filters-modal"
      v-model="showModal"
      title="Filters"
      size="md"
      hide-footer
    >
      <b-form>
        <b-form-group
          label="Category"
          label-for="services-filter-category"
        >
          <ImageTextSelect
            id="services-filter-category"
            :model-value="filterCategoryId"
            :options="categoryFilterOptions"
            :disabled="disabled"
            @update:model-value="$emit('update:filterCategoryId', $event)"
          />
        </b-form-group>

        <b-form-group
          label="Search by keyword"
          label-for="services-filter-search"
        >
          <b-form-input
            id="services-filter-search"
            :value="filterKeyword"
            placeholder="keyword"
            :disabled="disabled"
            @input="$emit('update:filterKeyword', normalizeInput($event))"
          />
        </b-form-group>
      </b-form>

      <div class="d-flex justify-content-between align-items-center mt-1">
        <div class="services-search-filters__status text-muted small d-flex align-items-center">
          <b-spinner
            v-if="showFooterLoading"
            small
            label="Loading..."
          />
          <span v-else-if="statusMessage">{{ statusMessage }}</span>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            variant="outline-primary"
            size="sm"
            class="mr-1"
            :disabled="disabled || !hasActiveFilters"
            @click="$emit('reset')"
          >
            Reset
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="showModal = false"
          >
            Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import ImageTextSelect from '@/views/Components/Elements/ImageTextSelect.vue'

export default {
  name: 'ServicesSearchFilters',
  components: {
    BBadge,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BModal,
    BSpinner,
    ImageTextSelect,
  },
  props: {
    filterCategoryId: {
      default: null,
    },
    filterKeyword: {
      type: String,
      default: '',
    },
    categoryFilterOptions: {
      type: Array,
      default: () => [],
    },
    hasActiveFilters: {
      type: Boolean,
      default: false,
    },
    isTableLoading: {
      type: Boolean,
      default: false,
    },
    filteredCount: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'update:filterCategoryId',
    'update:filterKeyword',
    'reset',
  ],
  data() {
    return {
      showModal: false,
      isFilterLoading: false,
      showResultStatus: false,
    }
  },
  computed: {
    filterSignature() {
      return [
        this.filterCategoryId,
        this.filterKeyword,
        this.hasActiveFilters,
      ].join('|')
    },
    showFooterLoading() {
      return this.showModal
        && this.hasActiveFilters
        && (this.isFilterLoading || this.isTableLoading)
    },
    statusMessage() {
      if (!this.showResultStatus || !this.hasActiveFilters || this.showFooterLoading) {
        return ''
      }

      const count = Number(this.filteredCount) || 0
      if (count === 1) {
        return 'Found one service'
      }

      return `Found ${count} services`
    },
  },
  watch: {
    filterSignature() {
      if (!this.hasActiveFilters) {
        this.isFilterLoading = false
        this.showResultStatus = false
        return
      }

      if (!this.showModal) {
        return
      }

      this.isFilterLoading = true
      this.showResultStatus = false
    },
    isTableLoading(loading) {
      if (!this.showModal || !this.hasActiveFilters) {
        return
      }

      if (loading) {
        this.isFilterLoading = true
        this.showResultStatus = false
        return
      }

      this.isFilterLoading = false
      this.showResultStatus = true
    },
    filteredCount() {
      if (!this.showModal || !this.hasActiveFilters || this.isTableLoading) {
        return
      }

      this.isFilterLoading = false
      this.showResultStatus = true
    },
  },
  methods: {
    normalizeInput(value) {
      return value && typeof value === 'object' && value.target
        ? value.target.value
        : value
    },
  },
}
</script>
